import { useMemo, useState } from 'react';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import type {
  AssignmentPurpose,
  CrewRow,
  CalendarAssignmentRow,
  CalendarTaskRow,
  CalendarWarrantyVisitRow,
} from '@gutterinv/shared';
import { taskStyle } from '../lib/task-styles.js';
import { WARRANTY_STYLE } from '../lib/warranty-styles.js';
import { postCalendarAssignment } from '../api/client.js';
import { Card } from './ui/card.js';
import { Button } from './ui/button.js';
import { cn } from '../lib/utils.js';
import { useEscapeKey } from '../hooks/useEscapeKey.js';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog.js';
import {
  DAY_LABELS,
  addDaysISO,
  daySpanInclusive,
  isPostInstallStatus,
  mixWithWhite,
  toISO,
  isToday,
} from '../lib/calendar-dates.js';
import type { DropTarget } from '../lib/calendar-drop.js';
import type { CalendarDragData } from '../hooks/useCalendarDnd.js';
import { packLanes } from '../lib/calendar-lanes.js';
import {
  buildMonthSegments,
  isJobSegment,
  monthSegmentKey,
} from '../lib/calendar-month-segments.js';
import { AssignmentEditModal } from './AssignmentEditModal.js';
import { PeopleMultiSelect } from './PeopleMultiSelect.js';

export function CalendarMonthView({
  days,
  anchor,
  assignmentsByDate,
  tasksByDate,
  onTaskClick,
  warrantyByDate,
  onWarrantyClick,
  crewColorMap,
  crews,
  onDayClick,
}: {
  days: Date[];
  anchor: Date;
  assignmentsByDate: Map<string, CalendarAssignmentRow[]>;
  tasksByDate: Map<string, CalendarTaskRow[]>;
  onTaskClick: (task: CalendarTaskRow) => void;
  warrantyByDate: Map<string, CalendarWarrantyVisitRow[]>;
  onWarrantyClick: (visit: CalendarWarrantyVisitRow) => void;
  crewColorMap: Map<string, string>;
  crews: CrewRow[];
  onDayClick: (dateISO: string) => void;
}) {
  const [expandedPillId, setExpandedPillId] = useState<string | null>(null);

  const weeks: Date[][] = useMemo(() => {
    const out: Date[][] = [];
    for (let i = 0; i < days.length; i += 7) out.push(days.slice(i, i + 7));
    return out;
  }, [days]);

  return (
    <Card className="overflow-hidden">
      <div className="grid grid-cols-7 border-b border-border">
        {DAY_LABELS.map((label) => (
          <div
            key={label}
            className="border-r border-border px-2 py-2 text-center text-xs font-medium uppercase tracking-wider text-muted-foreground last:border-r-0"
          >
            {label}
          </div>
        ))}
      </div>

      {weeks.map((week, wi) => (
        <MonthWeekRow
          key={wi}
          week={week}
          anchor={anchor}
          assignmentsByDate={assignmentsByDate}
          tasksByDate={tasksByDate}
          onTaskClick={onTaskClick}
          warrantyByDate={warrantyByDate}
          onWarrantyClick={onWarrantyClick}
          crewColorMap={crewColorMap}
          crews={crews}
          onDayClick={onDayClick}
          expandedPillId={expandedPillId}
          onExpandPill={setExpandedPillId}
          isLast={wi === weeks.length - 1}
        />
      ))}
    </Card>
  );
}

function MonthWeekRow({
  week,
  anchor,
  assignmentsByDate,
  tasksByDate,
  onTaskClick,
  warrantyByDate,
  onWarrantyClick,
  crewColorMap,
  crews,
  onDayClick,
  expandedPillId,
  onExpandPill,
  isLast,
}: {
  week: Date[];
  anchor: Date;
  assignmentsByDate: Map<string, CalendarAssignmentRow[]>;
  tasksByDate: Map<string, CalendarTaskRow[]>;
  onTaskClick: (task: CalendarTaskRow) => void;
  warrantyByDate: Map<string, CalendarWarrantyVisitRow[]>;
  onWarrantyClick: (visit: CalendarWarrantyVisitRow) => void;
  crewColorMap: Map<string, string>;
  crews: CrewRow[];
  onDayClick: (dateISO: string) => void;
  expandedPillId: string | null;
  onExpandPill: (id: string | null) => void;
  isLast: boolean;
}) {
  // Build a unified segment list for ALL feeds (job/estimate/task/warranty),
  // then BAND-pack: jobs/estimates in the top lanes (layout identical to
  // before, independent of secondary feeds), tasks/warranty offset below.
  const { laidOutSegments, perDayOverflow } = useMemo(() => {
    const weekISOs = week.map(toISO);
    const segs = buildMonthSegments({
      weekStart: week[0]!,
      weekISOs,
      assignmentsByDate,
      tasksByDate,
      warrantyByDate,
    });
    const jobPacked = packLanes(segs.filter(isJobSegment));
    const jobLanes = jobPacked.reduce((m, c) => Math.max(m, c.lane + 1), 0);
    const secPacked = packLanes(segs.filter((s) => !isJobSegment(s)));
    for (const s of secPacked) s.lane += jobLanes; // band below the job lanes
    const packed = [...jobPacked, ...secPacked];

    const MAX_LANES = 3;
    const overflow = new Map<string, number>(); // iso → hidden count
    const visible: typeof packed = [];
    for (const seg of packed) {
      if (seg.lane < MAX_LANES) {
        visible.push(seg);
      } else {
        for (let c = seg.startCol; c <= seg.endCol; c++) {
          const iso = weekISOs[c]!;
          overflow.set(iso, (overflow.get(iso) ?? 0) + 1);
        }
      }
    }
    return { laidOutSegments: visible, perDayOverflow: overflow };
  }, [week, assignmentsByDate, tasksByDate, warrantyByDate]);

  const laneCount = laidOutSegments.reduce((m, c) => Math.max(m, c.lane + 1), 0);

  return (
    <div
      className={cn('grid grid-cols-7', !isLast && 'border-b border-border')}
      style={{
        gridTemplateRows: `28px ${laneCount > 0 ? `repeat(${laneCount}, minmax(22px, auto))` : ''} auto`,
      }}
    >
      {/* Day cells span all rows so they act as drop targets / backdrops */}
      {week.map((d, di) => (
        <MonthDayCell
          key={toISO(d)}
          day={d}
          anchor={anchor}
          crews={crews}
          crewColorMap={crewColorMap}
          assignmentsForDay={assignmentsByDate.get(toISO(d)) ?? []}
          hasOtherEvents={
            (tasksByDate.get(toISO(d))?.length ?? 0) +
              (warrantyByDate.get(toISO(d))?.length ?? 0) >
            0
          }
          onDayClick={onDayClick}
          gridColumn={di + 1}
          totalRows={laneCount + 2}
        />
      ))}

      {/* "+N more" buttons — own grid row (last row) per day. */}
      {week.map((d, di) => {
        const iso = toISO(d);
        const overflowCount = perDayOverflow.get(iso) ?? 0;
        if (overflowCount === 0) return null;
        return (
          <button
            key={`overflow-${iso}`}
            type="button"
            onClick={() => onDayClick(iso)}
            style={{
              gridColumn: di + 1,
              gridRow: laneCount + 2,
              zIndex: 2,
            }}
            className="self-end justify-self-start px-1.5 pb-1 text-left text-[0.5625rem] font-medium text-primary hover:underline"
          >
            +{overflowCount} more
          </button>
        );
      })}

      {/* Segment bars — one lane-packed bar per event (all kinds). With
          pointer-based DnD, day-cell droppables are resolved by the pointer
          vs their rects (not DOM stacking), so chips no longer need to opt out
          of pointer events for drops on the days they cover. */}
      {laidOutSegments.map((seg, i) => {
        const span = seg.endCol - seg.startCol + 1;
        return (
          <div
            key={`${monthSegmentKey(seg)}-${i}`}
            style={{
              gridColumn: `${seg.startCol + 1} / span ${span}`,
              gridRow: seg.lane + 2, // row 1 reserved for day cell header
              zIndex: 1,
              alignSelf: 'center',
              margin: '0 2px',
            }}
          >
            {seg.kind === 'install' || seg.kind === 'estimate' ? (
              <JobSpanPill
                assignment={seg.assignment}
                crewColor={crewColorMap.get(seg.assignment.crew.id) ?? '#94a3b8'}
                isFirstSegment={seg.isFirstSegment}
                isLastSegment={seg.isLastSegment}
                isExpanded={expandedPillId === seg.assignment.id}
                onToggle={() =>
                  onExpandPill(expandedPillId === seg.assignment.id ? null : seg.assignment.id)
                }
                dragId={`${monthSegmentKey(seg)}-${i}`}
              />
            ) : seg.kind === 'task' ? (
              <TaskSpanPill task={seg.task} onClick={() => onTaskClick(seg.task)} />
            ) : seg.kind === 'warranty' ? (
              <WarrantySpanPill
                visit={seg.visit}
                isFirstSegment={seg.isFirstSegment}
                isLastSegment={seg.isLastSegment}
                onClick={() => onWarrantyClick(seg.visit)}
              />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function MonthDayCell({
  day,
  anchor,
  crews,
  crewColorMap,
  assignmentsForDay,
  hasOtherEvents,
  onDayClick,
  gridColumn,
  totalRows,
}: {
  day: Date;
  anchor: Date;
  crews: CrewRow[];
  crewColorMap: Map<string, string>;
  assignmentsForDay: CalendarAssignmentRow[];
  hasOtherEvents: boolean;
  onDayClick: (dateISO: string) => void;
  gridColumn: number;
  totalRows: number;
}) {
  const iso = toISO(day);
  const isCurrentMonth = day.getMonth() === anchor.getMonth();
  const isWeekend = day.getDay() === 0 || day.getDay() === 6;
  const crewIds = [...new Set(assignmentsForDay.map((a) => a.crew.id))];
  const isPast = iso < toISO(new Date());

  // Drop target. Past days are disabled — they never highlight or accept a drop;
  // the central resolver also rejects them, matching the create-modal date floor.
  const { setNodeRef, isOver } = useDroppable({
    id: `day-${iso}`,
    data: { kind: 'day', iso } satisfies DropTarget,
    disabled: isPast,
  });

  void crews;

  return (
    <div
      ref={setNodeRef}
      className={cn(
        'relative min-h-[80px] border-r border-border last:border-r-0 transition-colors',
        !isCurrentMonth && 'bg-muted/20',
        isWeekend && isCurrentMonth && 'bg-muted/10',
        isToday(day) && 'bg-primary/5',
        isOver && 'bg-primary/15 ring-2 ring-inset ring-primary/40',
      )}
      style={{
        gridColumn,
        gridRow: `1 / span ${totalRows}`,
      }}
    >
      <div className="flex items-start justify-between px-1.5 pt-1.5">
        <button
          onClick={() => (assignmentsForDay.length > 0 || hasOtherEvents) && onDayClick(iso)}
          className={cn(
            'text-sm font-medium',
            (assignmentsForDay.length > 0 || hasOtherEvents) && 'cursor-pointer hover:underline',
            isToday(day)
              ? 'inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground'
              : isCurrentMonth
                ? 'text-foreground'
                : 'text-muted-foreground/50',
          )}
        >
          {day.getDate()}
        </button>
        {crewIds.length > 0 && (
          <div className="flex items-center gap-1 mt-0.5">
            {crewIds.slice(0, 4).map((cid) => (
              <span
                key={cid}
                className="inline-block h-2 w-2 rounded-full"
                style={{ backgroundColor: crewColorMap.get(cid) ?? '#94a3b8' }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Single-day task pill (lane-packed like jobs). Draggable to reschedule to
// another day (drop onto a day/person cell); click opens the task edit modal.
function TaskSpanPill({ task, onClick }: { task: CalendarTaskRow; onClick: () => void }) {
  const st = taskStyle(task.taskType);
  const { setNodeRef, listeners, attributes, isDragging } = useDraggable({
    id: `task-${task.id}`,
    data: {
      payload: { kind: 'task-reassign', taskId: task.id, personIds: task.people.map((p) => p.id) },
      overlayLabel: st.label,
    } satisfies CalendarDragData,
  });
  return (
    <button
      type="button"
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onClick={onClick}
      title={st.label}
      style={{ touchAction: 'none', opacity: isDragging ? 0.4 : 1 }}
      className={cn(
        'flex w-full items-center gap-1 truncate rounded border px-1 py-0.5 text-left text-[0.625rem] font-medium cursor-grab active:cursor-grabbing',
        st.chip,
        task.completedAt && 'line-through opacity-60',
      )}
    >
      <st.Icon className="h-2.5 w-2.5 shrink-0" />
      <span className="truncate">{st.label}</span>
    </button>
  );
}

// Warranty-visit pill — spans its date range like a job; non-draggable.
function WarrantySpanPill({
  visit,
  isFirstSegment,
  isLastSegment,
  onClick,
}: {
  visit: CalendarWarrantyVisitRow;
  isFirstSegment: boolean;
  isLastSegment: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={`Warranty — ${visit.customerName} (${visit.address})`}
      className={cn(
        'flex w-full items-center gap-1 border-y px-1 py-0.5 text-left text-[0.625rem] font-medium',
        WARRANTY_STYLE.chip,
        isFirstSegment ? 'rounded-l border-l' : '',
        isLastSegment ? 'rounded-r border-r' : '',
        visit.completedAt && 'line-through opacity-60',
      )}
    >
      {isFirstSegment && <WARRANTY_STYLE.Icon className="h-2.5 w-2.5 shrink-0" />}
      <span className="truncate">
        {isFirstSegment ? `${WARRANTY_STYLE.label} · ${visit.customerName}` : ' '}
      </span>
    </button>
  );
}

function JobSpanPill({
  assignment,
  crewColor,
  isFirstSegment,
  isLastSegment,
  isExpanded,
  onToggle,
  dragId,
}: {
  assignment: CalendarAssignmentRow;
  crewColor: string;
  isFirstSegment: boolean;
  isLastSegment: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  /** Unique per rendered SEGMENT — a multi-week install renders multiple
   *  segments for one assignment, so the draggable id can't be assignment-only. */
  dragId?: string;
}) {
  useEscapeKey(isExpanded, onToggle);

  const isInstalled = isPostInstallStatus(assignment.job.status);
  const isEstimate = assignment.purpose === 'estimate';
  const isCompleted = assignment.completedAt !== null;
  const isCompletedEstimate = isEstimate && isCompleted;
  // Completed estimates + post-install installs are read-only (not draggable).
  const draggable = !isInstalled && !isCompletedEstimate;

  const { setNodeRef, listeners, attributes, isDragging } = useDraggable({
    id: dragId ?? `assignment-${assignment.id}`,
    data: {
      payload: {
        kind: 'reassign',
        jobId: assignment.job.id,
        purpose: assignment.purpose,
        oldAssignmentId: assignment.id,
        durationDays: daySpanInclusive(assignment.assignedDateStart, assignment.assignedDateEnd) - 1,
      },
      overlayLabel: assignment.job.displayName,
    } satisfies CalendarDragData,
    disabled: !draggable,
  });

  return (
    <div className="relative">
      <button
        ref={setNodeRef}
        {...listeners}
        {...attributes}
        onClick={onToggle}
        className={cn(
          'flex w-full items-center gap-1 px-1 py-0.5 text-left transition-colors hover:bg-muted/60',
          isFirstSegment ? 'rounded-l' : '',
          isLastSegment ? 'rounded-r' : '',
          isInstalled
            ? 'opacity-50 cursor-pointer'
            : draggable
              ? 'cursor-grab active:cursor-grabbing'
              : 'cursor-pointer',
          isCompleted && !isInstalled && 'opacity-60',
        )}
        style={{
          backgroundColor: isEstimate ? 'transparent' : mixWithWhite(crewColor, 0.18),
          borderTop: isEstimate ? `1px dashed ${crewColor}` : 'none',
          borderBottom: isEstimate ? `1px dashed ${crewColor}` : 'none',
          borderLeft: isFirstSegment
            ? isEstimate
              ? `1px dashed ${crewColor}`
              : `2px solid ${crewColor}`
            : 'none',
          borderRight: isLastSegment
            ? isEstimate
              ? `1px dashed ${crewColor}`
              : `2px solid ${crewColor}`
            : 'none',
          touchAction: draggable ? 'none' : undefined,
          opacity: isDragging ? 0.4 : undefined,
        }}
      >
        {isFirstSegment && (
          <span
            className="inline-block h-1.5 w-1.5 rounded-full shrink-0"
            style={{ backgroundColor: crewColor }}
          />
        )}
        {isCompleted && isFirstSegment && (
          <Check className="h-2.5 w-2.5 shrink-0 text-foreground/70" aria-label="completed" />
        )}
        <span
          className={cn(
            'truncate text-[0.625rem] leading-tight text-foreground',
            (isInstalled || isCompleted) && 'line-through',
          )}
        >
          {isFirstSegment && isEstimate && <span className="opacity-70">Est · </span>}
          {isFirstSegment ? assignment.job.displayName : ' '}
        </span>
      </button>

      {isExpanded && (
        <AssignmentEditModal assignment={assignment} onClose={onToggle} />
      )}
    </div>
  );
}

export function MonthDropPeoplePicker({
  jobId,
  dateISO,
  purpose,
  durationDays,
  onClose,
}: {
  jobId: string;
  dateISO: string;
  purpose: AssignmentPurpose;
  /** Original span minus one. 0 = single-day; N = reassigning an
   *  N+1-day install, in which case end = dateISO + N. */
  durationDays: number;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [personIds, setPersonIds] = useState<string[]>([]);

  const assignMutation = useMutation({
    mutationFn: postCalendarAssignment,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['calendar', 'assignments'] });
      void queryClient.invalidateQueries({ queryKey: ['calendar', 'unassigned'] });
      onClose();
    },
  });

  const handleSchedule = () => {
    if (personIds.length === 0) return;
    const endDate = durationDays > 0 ? addDaysISO(dateISO, durationDays) : dateISO;
    assignMutation.mutate({ jobId, personIds, startDate: dateISO, endDate, purpose });
  };

  const promptLabel =
    purpose === 'estimate'
      ? `Who's estimating on ${dateISO}?`
      : `Who's on this job for ${dateISO}?`;

  return (
    <Dialog open onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent className="w-80 max-w-[calc(100vw-2rem)] p-4 space-y-3">
        <DialogTitle className="text-sm font-semibold text-foreground">
          {promptLabel}
        </DialogTitle>
        <PeopleMultiSelect value={personIds} onChange={setPersonIds} />
        {assignMutation.error && (
          <div className="text-xs text-destructive">
            {assignMutation.error instanceof Error ? assignMutation.error.message : 'Failed'}
          </div>
        )}
        <div className="flex gap-2">
          <Button
            size="sm"
            className="flex-1 text-xs"
            onClick={handleSchedule}
            disabled={personIds.length === 0 || assignMutation.isPending}
          >
            {assignMutation.isPending ? 'Scheduling…' : 'Schedule'}
          </Button>
          <Button variant="ghost" size="sm" className="text-xs" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
